/* eslint-disable react/prop-types */
import React, { memo } from 'react';
import { View, TouchableOpacity } from 'react-native';
import { QText } from './index';
import { useTheme } from 'react-native-paper';
import { makeStyles } from '../hooks/MakeStylesHook';
import SecureImage from './SecureImage';
import ImageIcon from './Icons/ImageIcon';
import FileIcon from './Icons/FileIcon';

const useStyles = makeStyles((theme) => ({
  previewContainer: {
    width: 32,
    height: 32,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: theme.colors.primary + '15',
    justifyContent: 'center',
    alignItems: 'center',
  },
  documentPreview: {
    backgroundColor: theme.colors.primary + '20',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    minWidth: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  documentExtension: {
    fontSize: 8,
    fontWeight: 'bold',
    color: theme.colors.primary,
    textTransform: 'uppercase',
  },
  iconContainer: {
    padding: 4,
  },
}));

/**
 * Universal attachment preview component that shows appropriate preview for any file type
 */
const UniversalAttachmentPreview = ({
  attachment,
  onPress,
  style,
  showExtension = true,
}) => {
  const styles = useStyles();
  const theme = useTheme();

  if (!attachment) return null;

  const fileType = attachment.type || '';
  const fileName = attachment.name || '';
  const attachmentId = attachment.id;

  // Helper function to get file extension
  const getFileExtension = (filename) => {
    if (!filename) return '';
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
  };

  // Determine file category
  const isImage =
    fileType.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp'].includes(
      getFileExtension(fileName)
    );

  const isPDF =
    fileType.includes('pdf') || getFileExtension(fileName) === 'pdf';

  const isDocument =
    fileType.includes('document') ||
    fileType.includes('word') ||
    fileType.includes('text') ||
    ['doc', 'docx', 'txt', 'rtf'].includes(getFileExtension(fileName));

  const isSpreadsheet =
    fileType.includes('sheet') ||
    fileType.includes('excel') ||
    ['xls', 'xlsx', 'csv'].includes(getFileExtension(fileName));

  const renderPreview = () => {
    // For images, show actual image preview
    if (isImage && attachmentId) {
      return (
        <SecureImage
          attachmentId={attachmentId}
          style={[styles.previewContainer, style]}
          fallback={
            <View style={[styles.previewContainer, style]}>
              <ImageIcon width={16} height={16} color={theme.colors.primary} />
            </View>
          }
        />
      );
    }

    // For other file types, show appropriate icon with extension
    let icon = <FileIcon width={16} height={16} color={theme.colors.primary} />;
    let backgroundColor = theme.colors.primary + '15';
    let textColor = theme.colors.primary;

    // Customize colors based on file type
    if (isPDF) {
      backgroundColor = '#e53e3e15'; // Red tint for PDFs
      textColor = '#e53e3e';
    } else if (isDocument) {
      backgroundColor = '#3182ce15'; // Blue tint for documents
      textColor = '#3182ce';
    } else if (isSpreadsheet) {
      backgroundColor = '#38a16915'; // Green tint for spreadsheets
      textColor = '#38a169';
    }

    if (showExtension) {
      const extension = getFileExtension(fileName) || 'file';
      return (
        <View style={[styles.documentPreview, { backgroundColor }, style]}>
          <QText style={[styles.documentExtension, { color: textColor }]}>
            {extension}
          </QText>
        </View>
      );
    }

    return (
      <View style={[styles.previewContainer, { backgroundColor }, style]}>
        {icon}
      </View>
    );
  };

  if (onPress) {
    return (
      <TouchableOpacity onPress={onPress} style={style}>
        {renderPreview()}
      </TouchableOpacity>
    );
  }

  return renderPreview();
};

export default memo(UniversalAttachmentPreview);
